import { useCallback, useMemo } from 'react'
import type { Path } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

type LocationDescriptor<State = unknown> = string | (Partial<Path> & {
  state?: State
})

export interface AppNavigation {
  go(delta: number): void
  goBack(): void
  push(to: LocationDescriptor): void
  replace(to: LocationDescriptor): void
}

export function useAppNavigation(): AppNavigation {
  const navigate = useNavigate()

  const go = useCallback((delta: number) => {
    navigate(delta)
  }, [navigate])

  const goBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const push = useCallback((to: LocationDescriptor) => {
    navigateTo(navigate, to, false)
  }, [navigate])

  const replace = useCallback((to: LocationDescriptor) => {
    navigateTo(navigate, to, true)
  }, [navigate])

  return useMemo(() => ({
    go,
    goBack,
    push,
    replace,
  }), [go, goBack, push, replace])
}

function navigateTo(navigate: ReturnType<typeof useNavigate>, to: LocationDescriptor, replace: boolean) {
  if (typeof to === 'string') {
    navigate(to, { replace })
    return
  }

  const { state, ...path } = to
  navigate(path, { replace, state })
}
